/**
 * @fileoverview Hook customizado para refresh automático de sessão
 * @description Verifica sessão a cada 60 segundos e sincroniza carrinho/favoritos
 * @module useSessionRefresh
 * @since 1.0.0
 */

import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiRequestJson } from '../api/apiService.js';

/**
 * Hook para refresh automático de sessão com sincronização de dados.
 * Executa verificação a cada 60 segundos apenas em desktop.
 *
 * @hook
 * @param {Function} updateCart - Action do Redux para atualizar carrinho
 * @param {Function} updateFavorites - Action do Redux para atualizar favoritos
 * @param {boolean} [isMobile=false] - Se é dispositivo móvel (desabilita o hook)
 * @returns {void}
 *
 * @example
 * import { updateCart, updateFavorites } from '@/store/slices/userSlice';
 *
 * function App() {
 *   useSessionRefresh(updateCart, updateFavorites, false);
 *   return <div>...</div>;
 * }
 */
const useSessionRefresh = (updateCart, updateFavorites, isMobile = false) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Executa refresh de sessão e sincroniza dados.
   * Não executa na página de reset de senha ou em dispositivos móveis.
   * @async
   * @returns {Promise<void>}
   */
  const refreshSession = useCallback(async () => {
    // Não executar refresh na página de reset de senha ou em mobile
    if (location.pathname.startsWith('/reset-password/') || isMobile) {
      return;
    }

    // Verificar se há token antes de tentar refresh
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return; // Não há token, usuário não está autenticado
    }

    try {
      // Verificar sessão e obter dados de sincronização (híbrido mobile/desktop)
      const refreshData = await apiRequestJson('auth_session_refresh');
      console.log('session refresh: ', refreshData);

      // Sessão inválida - redirecionar para login
      if (!refreshData.success || !refreshData.data?.session_active) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
        return;
      }

      // Verificar se precisa atualizar carrinho via refresh
      if (refreshData.data?.shopcart?.needs_refresh && refreshData.data.shopcart.data) {
        console.log('✅ Atualizando carrinho via refresh...', refreshData.data.shopcart.data.length || 0, 'itens');
        dispatch(updateCart(refreshData.data.shopcart.data));
      }

      // Verificar se precisa atualizar favoritos via refresh
      if (refreshData.data?.favorities?.needs_refresh && refreshData.data.favorities.data) {
        console.log('✅ Atualizando favoritos via refresh...', refreshData.data.favorities.data.length || 0, 'itens');
        dispatch(updateFavorites(refreshData.data.favorities.data));
      }


    } catch (error) {
      console.error('Erro no refresh de sessão:', error);
      // Se erro 401, limpar tokens
      if (error.message?.includes('401') || error.message?.includes('Token')) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    }
  }, [dispatch, navigate, location.pathname, updateCart, updateFavorites, isMobile]);

  useEffect(() => {
    // Só executa se não for mobile
    if (isMobile) {
      return;
    }

    // Verificar se há token antes de executar
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return; // Sem log, é normal não ter token inicialmente
    }

    // Executar imediatamente ao montar apenas se autenticado
    refreshSession();

    // Configurar intervalo de 5 minutos (300000ms) - mais eficiente que 60s
    const interval = setInterval(refreshSession, 300000);

    // Cleanup ao desmontar
    return () => clearInterval(interval);
  }, [refreshSession, isMobile]); // Removido dependência desnecessária
};

export default useSessionRefresh;
